import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router";
import type { AnchorHTMLAttributes } from "react";

// Renders assistant markdown (bold, lists, code, links) instead of raw asterisks/dashes.
// In-app links (e.g. /assets/:assetTag, /tickets/:ticketNumber, /users/:wpUserId) the AI
// mentions are SPA-navigated instead of full-page-reloaded; external links open in a new tab.
export const MarkdownMessage = ({
  content,
  onInternalLinkClick,
}: {
  content: string;
  onInternalLinkClick?: () => void;
}) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        fontSize: "0.875rem",
        lineHeight: 1.57,
        "& > *:first-of-type": { mt: 0 },
        "& > *:last-child": { mb: 0 },
        "& p": { m: 0, mb: 1 },
        "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
        "& li": { mb: 0.25 },
        "& li > p": { m: 0, mb: 0 },
        "& strong": { fontWeight: 700 },
        "& a": { color: "inherit" },
        "& h1, & h2, & h3, & h4, & h5, & h6": { fontSize: "1em", fontWeight: 700, m: 0, mb: 1 },
        "& code": {
          fontFamily: "monospace",
          fontSize: "0.8125rem",
          bgcolor: "rgba(0,0,0,0.08)",
          borderRadius: 0.5,
          px: 0.5,
          py: 0.15,
        },
        "& pre": {
          bgcolor: "rgba(0,0,0,0.08)",
          borderRadius: 1,
          p: 1,
          overflowX: "auto",
          mb: 1,
          "& code": { bgcolor: "transparent", p: 0 },
        },
        "& table": { borderCollapse: "collapse", mb: 1 },
        "& th, & td": { border: "1px solid currentColor", opacity: 0.9, px: 0.75, py: 0.25 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) => {
            const isInternal = !!href && href.startsWith("/");
            return (
              <a
                href={href}
                target={isInternal ? undefined : "_blank"}
                rel={isInternal ? undefined : "noopener noreferrer"}
                onClick={
                  isInternal
                    ? (e) => {
                        e.preventDefault();
                        onInternalLinkClick?.();
                        navigate(href!);
                      }
                    : undefined
                }
                {...props}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};
